import {
	ArrowDropDown,
	ArrowRight,
	CheckCircle,
	Edit,
	ListAlt,
	MoreVert,
	Warning,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Chip, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import {
	DataGridPremium,
	useGridApiRef,
	type GridCellParams,
	type GridColDef,
	type GridRenderCellParams,
	type GridRowId,
	type GridRowParams,
} from "@mui/x-data-grid-premium";
import { ProjectStatus, ProjectType, ValidationStatus } from "@repo/server-api";
import { useCurrencyFormatter } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BudgetProgressBar } from "./BudgetProgressBar";
import type { ProjectSummary } from "../types/ProjectTypes";

export type PaginationModel = {
	page: number;
	pageSize: number;
};

export type ProjectDataGridProps = {
	rows: ProjectSummary[];
	subRows: ProjectSummary[];
	rowCount: number;
	isLoading: boolean;
	matchedIds?: string[];

	showInactive: boolean;

	paginationModel: PaginationModel;
	onPaginationModelChange: (model: PaginationModel) => void;

	onExpandRow: (id: string) => Promise<void>;
	onEditProject: (project: ProjectSummary) => void;
	onDeleteProject: (id: string) => void;
	onAddSubProject: (parent: ProjectSummary) => void;
	onOpenOffers: (project: ProjectSummary) => void;
	onChangeStatus: (project: ProjectSummary) => void;
	onCreateOffer: (project: ProjectSummary) => void;
};

export const ProjectDataGrid = ({
	rows,
	subRows,
	rowCount,
	isLoading,
	matchedIds = [],
	showInactive,
	paginationModel,
	onPaginationModelChange,
	onExpandRow,
	onEditProject,
	onDeleteProject,
	onAddSubProject,
	onOpenOffers,
	onChangeStatus,
	onCreateOffer,
}: ProjectDataGridProps) => {
	const { t, i18n } = useTranslation();
	const apiRef = useGridApiRef();
	const formatCurrency = useCurrencyFormatter();

	// Three-dot menu state
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [menuRow, setMenuRow] = useState<ProjectSummary | null>(null);

	// Track if we've auto-expanded for the current search
	const hasAutoExpandedRef = useRef(false);
	const prevMatchedIdsRef = useRef<string[]>([]);

	// Combined rows
	const allRows = useMemo(() => [...rows, ...subRows], [rows, subRows]);

	// Set of matched IDs for quick lookup
	const matchedIdSet = useMemo(() => new Set(matchedIds), [matchedIds]);

	// Highlighting for search matches
	const getCellClassName = useCallback(
		(params: GridCellParams<ProjectSummary>) => {
			const classes: string[] = [];

			if (matchedIdSet.has(params.row.id)) {
				classes.push("search-match-highlight");
			}

			if (params.row.isDeleted) {
				classes.push("deleted-row");
			}

			return classes.join(" ");
		},
		[matchedIdSet],
	);

	// Handle tree expansion
	const handleExpandClick = useCallback(
		async (params: GridRenderCellParams) => {
			if (params.row.type !== "group" || !apiRef.current) return;

			const rowNode = apiRef.current.getRowNode(params.row.id);
			const isExpanded =
				rowNode && "childrenExpanded" in rowNode ? rowNode.childrenExpanded : false;
			const hasChildren =
				rowNode &&
				"children" in rowNode &&
				Array.isArray(rowNode.children) &&
				rowNode.children.length > 0;

			if (!isExpanded && !hasChildren) {
				await onExpandRow(params.row.id.toString());
				// Small delay to ensure rows are rendered
				setTimeout(() => {
					const updatedRowNode = apiRef.current?.getRowNode(params.row.id);
					if (
						updatedRowNode &&
						"children" in updatedRowNode &&
						Array.isArray(updatedRowNode.children) &&
						updatedRowNode.children.length > 0
					) {
						apiRef.current?.setRowChildrenExpansion(params.row.id, true);
					}
				}, 50);
			} else {
				apiRef.current.setRowChildrenExpansion(params.row.id, !isExpanded);
			}
		},
		[apiRef, onExpandRow],
	);

	// Auto-expand parents when search matches change
	useEffect(() => {
		// Check if matchedIds actually changed
		const matchedIdsChanged =
			matchedIds.length !== prevMatchedIdsRef.current.length ||
			matchedIds.some((id, i) => prevMatchedIdsRef.current[i] !== id);

		if (!matchedIdsChanged) return;
		prevMatchedIdsRef.current = matchedIds;

		if (matchedIds.length === 0 || !apiRef.current || allRows.length === 0) {
			hasAutoExpandedRef.current = false;
			return;
		}

		// Don't re-expand if we've already done it for these matches
		if (hasAutoExpandedRef.current) return;

		// Find all ancestors of matched rows and expand them
		const parentsToExpand = new Set<string>();

		allRows.forEach((row) => {
			if (matchedIdSet.has(row.id) && row.path.length > 1) {
				// Add all ancestors (all path elements except the last one, which is the row itself)
				row.path.slice(0, -1).forEach((ancestorId) => {
					parentsToExpand.add(ancestorId);
				});
			}
		});

		// Expand parents with a small delay to ensure API is ready
		setTimeout(() => {
			parentsToExpand.forEach((id) => {
				const rowNode = apiRef.current?.getRowNode(id);
				if (rowNode) {
					apiRef.current?.setRowChildrenExpansion(id, true);
				}
			});
			hasAutoExpandedRef.current = true;
		}, 100);
	}, [matchedIds, allRows, matchedIdSet, apiRef]);

	// Reset auto-expand flag when matchedIds become empty (search cleared)
	useEffect(() => {
		if (matchedIds.length === 0) {
			hasAutoExpandedRef.current = false;
		}
	}, [matchedIds]);

	// Auto-size columns to fit content after data loads
	useEffect(() => {
		if (!isLoading && allRows.length > 0 && apiRef.current) {
			const timer = setTimeout(() => {
				apiRef.current?.autosizeColumns({
					columns: ["projectType", "range", "status"],
					includeHeaders: true,
					includeOutliers: true,
				});
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isLoading, allRows, apiRef]);

	// Column definitions
	const columns: GridColDef<ProjectSummary>[] = useMemo(
		() => [
			{
				field: "number",
				headerName: t("project-number"),
				width: 150,
			},
			{
				field: "projectType",
				headerName: t("type"),
				width: 100,
				renderCell(params: GridCellParams<ProjectSummary>) {
					return params.row.projectType === ProjectType.Tender ? t("tender") : t("project");
				},
			},
			{
				field: "validationStatus",
				headerName: t("status"),
				width: 80,
				sortable: false,
				renderCell(params: GridCellParams<ProjectSummary>) {
					const isValid = params.row.validationStatus === ValidationStatus.Valid;
					return (
						<Tooltip title={isValid ? t("project-valid") : t("project-invalid")}>
							<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
								{isValid ? (
									<CheckCircle color="success" fontSize="small" />
								) : (
									<Warning color="warning" fontSize="small" />
								)}
							</Box>
						</Tooltip>
					);
				},
			},
			...(showInactive
				? [
						{
							field: "isActive",
							headerName: t("Active"),
							width: 120,
							sortable: false,
							renderCell(params: GridCellParams<ProjectSummary>) {
								const isActive = !params.row.isDeleted;
								return (
									<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
										<Chip
											label={isActive ? t("Active") : t("Inactive")}
											color={isActive ? "success" : "default"}
											size="small"
										/>
									</Box>
								);
							},
						} as GridColDef<ProjectSummary>,
					]
				: []),
			{
				field: "budget",
				headerName: t("budget"),
				width: 100,
				renderCell(params: GridCellParams<ProjectSummary>) {
					const { budgetStatus } = params.row;

					if (!budgetStatus || budgetStatus.totalBudgetAmount === 0) {
						return (
							<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
								<Typography variant="body2" color="text.secondary">
									---
								</Typography>
							</Box>
						);
					}

					return (
						<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
							<Typography variant="body2">
								{formatCurrency(budgetStatus.totalBudgetAmount)}
							</Typography>
						</Box>
					);
				},
			},
			{
				field: "budgetUtilization",
				headerName: t("budget-utilization"),
				width: 150,
				sortable: false,
				renderCell(params: GridCellParams<ProjectSummary>) {
					const { budgetStatus } = params.row;

					if (!budgetStatus || budgetStatus.totalBudgetAmount === 0) {
						return (
							<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
								<Typography variant="body2" color="text.secondary">
									---
								</Typography>
							</Box>
						);
					}

					const isOverBudget = budgetStatus.utilizationPercent > 100;

					return (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								height: "100%",
							}}>
							<Typography
								variant="caption"
								color={isOverBudget ? "error" : "text.secondary"}
								fontWeight={isOverBudget ? "bold" : "normal"}>
								{budgetStatus.utilizationPercent.toFixed(0)}%
							</Typography>
							<BudgetProgressBar
								utilizationPercent={budgetStatus.utilizationPercent}
								showPercentage={false}
							/>
						</Box>
					);
				},
			},
			{
				field: "range",
				headerName: t("date-range"),
				width: 210,
				renderCell(params: GridCellParams) {
					const format = i18n.language === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";
					return `${dayjs(params.row.startDate).format(format)} - ${dayjs(
						params.row.endDate,
					).format(format)}`;
				},
			},
			{
				field: "status",
				headerName: t("status"),
				width: 200,
				renderCell(params: GridCellParams<ProjectSummary>) {
					const status = params.row.status;
					const chipSx = (() => {
						switch (status) {
							case ProjectStatus.Draft:
								return { backgroundColor: "#9E9E9E", color: "#fff" };
							case ProjectStatus.OfferCreated:
								return { backgroundColor: "#FFC107", color: "#fff" };
							case ProjectStatus.OfferSent:
								return { backgroundColor: "#FF9800", color: "#fff" };
							case ProjectStatus.Active:
								return { backgroundColor: "#4CAF50", color: "#fff" };
							case ProjectStatus.Rejected:
								return { backgroundColor: "#F44336", color: "#fff" };
							case ProjectStatus.Canceled:
								return { backgroundColor: "#616161", color: "#fff" };
							case ProjectStatus.Completed:
								return { backgroundColor: "#607D8B", color: "#fff" };
							default:
								return { backgroundColor: "#9E9E9E", color: "#fff" };
						}
					})();
					return (
						<Box display="flex" alignItems="center" height="100%" gap={0.5}>
							<Chip
								label={status ? t(`project-status.${status.toLocaleLowerCase()}`) : ""}
								size="small"
								sx={chipSx}
							/>
							<Tooltip title={t("project-offers")}>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										onOpenOffers(params.row);
									}}
									data-testid="open-project-offers-button">
									<ListAlt fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 230,
				renderCell: (params: GridCellParams<ProjectSummary>) => {
					return (
						<div>
							<Button
								onClick={() => onAddSubProject(params.row)}
								data-testid="add-sub-project-button">
								<AddIcon />
							</Button>
							<Button onClick={() => onEditProject(params.row)} data-testid="edit-project-button">
								<Edit />
							</Button>
							<Button
								onClick={() => onDeleteProject(params.row.id)}
								data-testid="delete-project-button">
								<DeleteIcon />
							</Button>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									setMenuAnchorEl(e.currentTarget);
									setMenuRow(params.row);
								}}
								data-testid="project-actions-menu-button">
								<MoreVert fontSize="small" />
							</IconButton>
						</div>
					);
				},
			},
		],
		[
			t,
			i18n.language,
			showInactive,
			formatCurrency,
			onAddSubProject,
			onEditProject,
			onDeleteProject,
			onOpenOffers,
			onChangeStatus,
		],
	);

	// Grouping column definition for tree data
	const groupingColDef = useMemo(
		() => ({
			headerName: t("name"),
			width: 200,
			flex: 1,
			leafField: "name",
			renderCell: (params: GridRenderCellParams<ProjectSummary>) => {
				const row = params.row;
				if (!row) return null;

				const depth = params.rowNode?.depth ?? 0;
				const rowNode = apiRef.current?.getRowNode(params.id);
				const isExpanded =
					rowNode && "childrenExpanded" in rowNode ? rowNode.childrenExpanded : false;
				const isMatch = matchedIdSet.has(row.id);

				return (
					<Box
						sx={{
							ml: depth * 2.5,
							display: "flex",
							alignItems: "center",
							height: "100%",
							fontWeight: isMatch ? 600 : 400,
						}}>
						{row.isGroup ? (
							<IconButton
								size="small"
								onClick={() => handleExpandClick(params)}
								data-testid="expand-row-button"
								sx={{ mr: 0.5 }}>
								{isExpanded ? <ArrowDropDown /> : <ArrowRight />}
							</IconButton>
						) : (
							<Box sx={{ width: 28 }} />
						)}
						<Typography variant="body2" noWrap>
							{row.name}
						</Typography>
					</Box>
				);
			},
		}),
		[t, apiRef, handleExpandClick, matchedIdSet],
	);

	return (
		<>
			<DataGridPremium
				apiRef={apiRef}
				rows={allRows}
				columns={columns}
				rowCount={rowCount}
				localeText={{
					paginationRowsPerPage: t("pagination.rows-per-page", { ns: "common" }),
					MuiTablePagination: {
						labelRowsPerPage: t("pagination.rows-per-page", { ns: "common" }),
					},
				}}
				// Pagination
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={onPaginationModelChange}
				pageSizeOptions={[10, 20, 25, 50, 100]}
				// Tree Data
				treeData
				getTreeDataPath={(row: ProjectSummary) => row.path || []}
				groupingColDef={groupingColDef}
				defaultGroupingExpansionDepth={-1}
				// Styling
				getCellClassName={getCellClassName}
				getRowId={(row: GridRowParams | ProjectSummary) => row.id as GridRowId}
				// Loading
				loading={isLoading}
				// Other
				disableRowSelectionOnClick
				className="table"
				sx={{
					border: "none",
					backgroundColor: "transparent",
					"& .search-match-highlight": {
						backgroundColor: "rgba(25, 118, 210, 0.08)",
					},
					"& .deleted-row": {
						opacity: 0.5,
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
				}}
				data-testid="project-data-grid"
			/>
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}>
				<MenuItem
					onClick={() => {
						if (menuRow) onChangeStatus(menuRow);
						setMenuAnchorEl(null);
					}}
					data-testid="change-status-menu-item">
					{t("change-project-status")}
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (menuRow) onCreateOffer(menuRow);
						setMenuAnchorEl(null);
					}}
					data-testid="create-offer-menu-item">
					{t("create-offer")}
				</MenuItem>
			</Menu>
		</>
	);
};
